import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageHeader, ErrorState } from '@ui-kit';
import { useMyProfile } from '../hooks/useMyProfile';
import { IdentityCard } from '../components/IdentityCard';
import { BasicInfoTab } from '../components/BasicInfoTab';
import { PasswordChangeTab } from '../components/PasswordChangeTab';
import { AvatarTab } from '../components/AvatarTab';
import { AdminToolsSection } from '../components/AdminToolsSection';
import type { ApiError } from '../types/myProfile';

type TabKey = 'basic' | 'password' | 'avatar';

// Skeleton placeholder for loading state
function ProfileSkeleton() {
  const { t } = useTranslation();
  function Bone({ width, height }: { width: string | number; height: number }) {
    return (
      <div
        style={{
          width,
          height,
          borderRadius: 'var(--radius-sm)',
          background: 'var(--color-surface-elevated)',
          animation: 'pulse 1.5s ease-in-out infinite',
        }}
      />
    );
  }

  return (
    <div>
      {/* Identity card skeleton */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--spacing-6)',
          padding: 'var(--spacing-6)',
          marginBottom: 'var(--spacing-6)',
          background: 'var(--color-surface-muted)',
          border: '1px solid var(--color-surface-border)',
          borderRadius: 'var(--radius-lg)',
        }}
        aria-label={t('myProfile.page.loading')}
        aria-busy="true"
      >
        {/* Avatar circle skeleton */}
        <div
          style={{
            width: 96,
            height: 96,
            borderRadius: 'var(--radius-full)',
            background: 'var(--color-surface-elevated)',
            flexShrink: 0,
            animation: 'pulse 1.5s ease-in-out infinite',
          }}
        />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 'var(--spacing-2)' }}>
          <Bone width="40%" height={24} />
          <Bone width="60%" height={16} />
          <div style={{ display: 'flex', gap: 'var(--spacing-2)' }}>
            <Bone width={80} height={20} />
            <Bone width={80} height={20} />
            <Bone width={100} height={20} />
          </div>
        </div>
      </div>

      {/* Tab skeleton */}
      <div style={{ display: 'flex', gap: 'var(--spacing-2)', marginBottom: 'var(--spacing-6)' }}>
        {[80, 100, 90].map((w, i) => (
          <Bone key={i} width={w} height={36} />
        ))}
      </div>

      {/* Form fields skeleton */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-4)', maxWidth: 520 }}>
        {[1, 2, 3].map((i) => (
          <div key={i} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-1)' }}>
            <Bone width={80} height={14} />
            <Bone width="100%" height={38} />
          </div>
        ))}
      </div>
    </div>
  );
}

// Simple toast notification
interface Toast {
  id: number;
  message: string;
  type: 'success' | 'error';
}

let toastIdCounter = 0;

export function MyProfilePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabKey>('basic');
  const [toasts, setToasts] = useState<Toast[]>([]);

  const profileQuery = useMyProfile();

  const addToast = useCallback((message: string, type: 'success' | 'error' = 'success') => {
    const id = ++toastIdCounter;
    setToasts((prev) => [...prev, { id, message, type }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 5000);
  }, []);

  // Handle 401 — redirect to login
  useEffect(() => {
    if (profileQuery.isError) {
      const err = profileQuery.error as unknown as ApiError;
      if (err?.code?.startsWith('AUTH-401')) {
        void navigate('/login');
      }
    }
  }, [profileQuery.isError, profileQuery.error, navigate]);

  const isLoading = profileQuery.isLoading;
  const isError = profileQuery.isError && !((profileQuery.error as unknown as ApiError)?.code?.startsWith('AUTH-401'));
  const profile = profileQuery.data;

  const TAB_LABELS: Record<TabKey, string> = {
    basic: t('myProfile.tabs.basic'),
    password: t('myProfile.tabs.password'),
    avatar: t('myProfile.tabs.avatar'),
  };

  const tabStyle = (key: TabKey): React.CSSProperties => ({
    padding: 'var(--spacing-2) var(--spacing-4)',
    borderRadius: 'var(--radius-md)',
    border: activeTab === key
      ? '1px solid var(--color-brand-primary)'
      : '1px solid var(--color-surface-border)',
    background: activeTab === key
      ? 'color-mix(in srgb, var(--color-brand-primary) 12%, var(--color-surface-muted))'
      : 'var(--color-surface-elevated)',
    color: activeTab === key ? 'var(--color-brand-primary)' : 'var(--color-text-secondary)',
    fontWeight: activeTab === key ? 700 : 400,
    fontSize: 'var(--font-size-sm)',
    cursor: 'pointer',
    fontFamily: 'var(--font-family-sans)',
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
  });

  return (
    <div style={{ padding: 'var(--spacing-6)' }}>
      {/* Toast stack */}
      {toasts.length > 0 && (
        <div
          aria-live="polite"
          aria-atomic="false"
          style={{
            position: 'fixed',
            top: 'var(--spacing-4)',
            right: 'var(--spacing-4)',
            zIndex: 1000,
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--spacing-2)',
          }}
        >
          {toasts.map((toast) => (
            <div
              key={toast.id}
              role="status"
              style={{
                padding: 'var(--spacing-3) var(--spacing-4)',
                background: toast.type === 'success'
                  ? 'color-mix(in srgb, var(--color-success) 12%, var(--color-surface-background))'
                  : 'color-mix(in srgb, var(--color-danger) 12%, var(--color-surface-background))',
                border: `1px solid ${toast.type === 'success' ? 'var(--color-success)' : 'var(--color-danger)'}`,
                borderRadius: 'var(--radius-md)',
                boxShadow: 'var(--shadow-md)',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--color-text-primary)',
                maxWidth: 360,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 'var(--spacing-3)',
              }}
            >
              <span>{toast.message}</span>
              <button
                type="button"
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== toast.id))}
                aria-label={t('myProfile.page.toastClose')}
                style={{
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--color-text-muted)',
                  fontSize: 'var(--font-size-lg)',
                  padding: 0,
                  lineHeight: 1,
                  flexShrink: 0,
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Page header */}
      <PageHeader
        title={t('myProfile.page.title')}
        breadcrumb={<span>{t('common.home')}</span>}
        actions={
          <button
            type="button"
            onClick={() => void navigate('/audit-logs/me')}
            style={{
              padding: 'var(--spacing-2) var(--spacing-4)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: 500,
              color: 'var(--color-text-secondary)',
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              cursor: 'pointer',
            }}
            aria-label={t('myProfile.page.activityAriaLabel')}
          >
            {t('myProfile.page.activityButton')}
          </button>
        }
      />

      <div style={{ paddingTop: 'var(--spacing-6)' }}>
        {/* loading — skeleton */}
        {isLoading && <ProfileSkeleton />}

        {/* error */}
        {isError && (
          <ErrorState
            title={t('myProfile.error.loadFailed')}
            message={
              (profileQuery.error as unknown as ApiError)?.message ??
              t('myProfile.error.loadFailedDesc')
            }
            onRetry={() => void profileQuery.refetch()}
          />
        )}

        {/* default — loaded */}
        {!isLoading && !isError && profile && (
          <>
            {/* Identity summary card */}
            <IdentityCard profile={profile} />

            {/* Tab navigation */}
            <nav
              aria-label={t('myProfile.page.tabNav')}
              style={{
                display: 'flex',
                gap: 'var(--spacing-2)',
                marginBottom: 'var(--spacing-6)',
                flexWrap: 'wrap',
              }}
            >
              {(Object.entries(TAB_LABELS) as [TabKey, string][]).map(([key, label]) => (
                <button
                  key={key}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === key}
                  aria-controls={`tabpanel-${key}`}
                  id={`tab-${key}`}
                  onClick={() => setActiveTab(key)}
                  onKeyDown={(e) => {
                    const keys = Object.keys(TAB_LABELS) as TabKey[];
                    const idx = keys.indexOf(key);
                    if (e.key === 'ArrowRight') {
                      setActiveTab(keys[(idx + 1) % keys.length]);
                    } else if (e.key === 'ArrowLeft') {
                      setActiveTab(keys[(idx - 1 + keys.length) % keys.length]);
                    }
                  }}
                  style={tabStyle(key)}
                >
                  {label}
                </button>
              ))}
            </nav>

            {/* Tab panels */}
            <div
              id="tabpanel-basic"
              role="tabpanel"
              aria-labelledby="tab-basic"
              hidden={activeTab !== 'basic'}
              style={{
                background: 'var(--color-surface-background)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--spacing-6)',
              }}
            >
              {activeTab === 'basic' && (
                <BasicInfoTab
                  profile={profile}
                  onSuccess={(msg) => addToast(msg, 'success')}
                />
              )}
            </div>

            <div
              id="tabpanel-password"
              role="tabpanel"
              aria-labelledby="tab-password"
              hidden={activeTab !== 'password'}
              style={{
                background: 'var(--color-surface-background)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--spacing-6)',
              }}
            >
              {activeTab === 'password' && (
                <PasswordChangeTab
                  onSuccess={(msg) => addToast(msg, 'success')}
                />
              )}
            </div>

            <div
              id="tabpanel-avatar"
              role="tabpanel"
              aria-labelledby="tab-avatar"
              hidden={activeTab !== 'avatar'}
              style={{
                background: 'var(--color-surface-background)',
                border: '1px solid var(--color-surface-border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--spacing-6)',
              }}
            >
              {activeTab === 'avatar' && (
                <AvatarTab
                  profile={profile}
                  onSuccess={(msg) => addToast(msg, 'success')}
                />
              )}
            </div>

            {/* 관리자 도구 — ROLE_ADMIN 에게만 표시 */}
            <AdminToolsSection addToast={addToast} />
          </>
        )}

        {/* empty — profile data is null (shouldn't happen for authenticated users) */}
        {!isLoading && !isError && !profile && (
          <div
            style={{
              padding: 'var(--spacing-12)',
              textAlign: 'center',
              background: 'var(--color-surface-muted)',
              border: '1px dashed var(--color-surface-border)',
              borderRadius: 'var(--radius-lg)',
            }}
          >
            <h3 style={{ margin: 0, color: 'var(--color-text-primary)' }}>
              {t('myProfile.empty.title')}
            </h3>
            <p style={{ marginTop: 'var(--spacing-2)', color: 'var(--color-text-secondary)' }}>
              {t('myProfile.empty.description')}
            </p>
            <button
              type="button"
              onClick={() => void navigate('/login')}
              style={{
                marginTop: 'var(--spacing-4)',
                padding: 'var(--spacing-2) var(--spacing-4)',
                background: 'var(--color-brand-primary)',
                color: 'var(--color-text-inverse)',
                border: 'none',
                borderRadius: 'var(--radius-md)',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              {t('myProfile.empty.loginButton')}
            </button>
          </div>
        )}
      </div>

      {/* Responsive — mobile breakpoint handled via layout shift */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
        @media (max-width: 767px) {
          #tabpanel-basic,
          #tabpanel-password,
          #tabpanel-avatar {
            padding: var(--spacing-4) !important;
          }
        }
      `}</style>
    </div>
  );
}
